/**
 * MediaPreviewModal — DE-171 in-browser content preview (video/audio).
 *
 * Shared by the content library (ItemCard thumbnail click) and the device
 * drill-down (per-file ▶). Opens a lightbox showing the static poster with the
 * native play control — it does NOT autoplay and uses `preload="none"`, so the
 * media bytes download only when the operator presses play (keeps pages fast).
 * The presigned URL is fetched once on open (never eagerly for the grid/list).
 *
 * Plays the canonical cloud copy (= what's deployed to devices). H.264/AAC MP4
 * + MP3 play natively; a stray undecodable file (e.g. HEVC) trips onError → a
 * graceful "can't preview" + Download link.
 */
import { useEffect, useRef, useState } from 'react';
import { useGetMediaUrl } from '../api/hooks';

export function MediaPreviewModal({
  assetId,
  kind,
  title,
  posterUrl,
  description,
  onClose,
}: {
  assetId: string;
  kind: string;            // 'video' | 'audio'
  title: string;
  posterUrl?: string;      // poster/cover (optional — device side omits it)
  description?: string;    // sidecar description (testimonials); shown under the title
  onClose: () => void;
}) {
  const media = useGetMediaUrl();
  const mutate = media.mutate;
  const closeRef = useRef<HTMLButtonElement>(null);
  const [playbackError, setPlaybackError] = useState(false);
  const isAudio = kind === 'audio';
  const url = media.data?.url;

  // Presign on open (once per asset). Fresh URL each time the modal mounts.
  useEffect(() => {
    setPlaybackError(false);
    mutate({ assetId });
  }, [assetId, mutate]);

  // Escape to close + focus management (no shared util exists).
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-modal-top"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="card w-full max-w-3xl max-h-[90vh] overflow-auto space-y-3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-baseline justify-between gap-3">
          <h2 className="font-medium truncate" title={title}>{title}</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close preview"
            className="shrink-0 text-sm text-muted hover:text-fg"
          >
            Close ✕
          </button>
        </div>

        {description && <p className="text-sm text-muted -mt-1">{description}</p>}

        {/* presigning */}
        {media.isPending && <div className="aspect-video rounded bg-soft animate-pulse" />}

        {/* presign failed (404 / network) */}
        {media.isError && (
          <div className="space-y-2 text-sm text-warn">
            <p>Couldn’t load this media{(media.error as Error)?.message ? `: ${(media.error as Error).message}` : '.'}</p>
            <button
              type="button"
              onClick={() => { setPlaybackError(false); mutate({ assetId }); }}
              className="px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft"
            >
              Retry
            </button>
          </div>
        )}

        {/* media decode failed (e.g. HEVC the browser can't play) */}
        {url && playbackError && (
          <div className="space-y-2 text-sm text-warn">
            <p>Can’t preview this format in the browser.</p>
            <a
              href={url}
              download
              className="inline-block px-3 py-1.5 border border-border rounded-md text-sm font-medium hover:bg-soft"
            >
              Download
            </a>
          </div>
        )}

        {/* video */}
        {url && !isAudio && !playbackError && (
          <video
            src={url}
            poster={posterUrl}
            controls
            preload="none"
            onError={() => setPlaybackError(true)}
            className="w-full max-h-[78vh] rounded bg-black"
          />
        )}

        {/* audio — cover art + native controls */}
        {url && isAudio && !playbackError && (
          <div className="space-y-3">
            <div className="aspect-video rounded bg-soft flex items-center justify-center overflow-hidden">
              {posterUrl ? (
                <img src={posterUrl} alt={title} className="h-full w-full object-contain" />
              ) : (
                <span className="text-5xl text-muted/50" aria-hidden="true">♪</span>
              )}
            </div>
            <audio
              src={url}
              controls
              preload="none"
              onError={() => setPlaybackError(true)}
              className="w-full"
            />
          </div>
        )}
      </div>
    </div>
  );
}
